import { useState, useEffect } from 'react';

export const MIN_WORKSPACE_WIDTH = 1024;
export const MIN_WORKSPACE_HEIGHT = 600;
// The fully unfolded Galaxy Z Fold in landscape is the smallest supported workspace.
// Its browser chrome can reduce the usable CSS height below 600px, so this is a
// deliberately narrow exception rather than a lower global workspace minimum.
export const MIN_WORKSPACE_LANDSCAPE_WIDTH = 829;
export const MIN_FOLD_WORKSPACE_HEIGHT = 500;

export interface WorkspaceViewportState {
  isSupported: boolean;
  isPortraitTablet: boolean;
}

export function evaluateWorkspaceViewport(width: number, height: number): WorkspaceViewportState {
  const isLandscape = width > height;
  const isFoldLandscape =
    isLandscape &&
    width >= MIN_WORKSPACE_LANDSCAPE_WIDTH &&
    width < MIN_WORKSPACE_WIDTH &&
    height >= MIN_FOLD_WORKSPACE_HEIGHT;
  const isDesktopWorkspace = width >= MIN_WORKSPACE_WIDTH && height >= MIN_WORKSPACE_HEIGHT;
  const isSupported = isDesktopWorkspace || isFoldLandscape;

  // In portrait/square mode, consider it a rotatable tablet/foldable if rotating to landscape
  // (height becomes width, width becomes height) would satisfy the landscape requirements.
  const isPortraitTablet =
    !isSupported &&
    height >= MIN_WORKSPACE_LANDSCAPE_WIDTH &&
    width >= MIN_FOLD_WORKSPACE_HEIGHT;

  return { isSupported, isPortraitTablet };
}

export function useSupportedWorkspaceViewport(): WorkspaceViewportState {
  const [viewportState, setViewportState] = useState<WorkspaceViewportState>(() => {
    // The app is client-rendered. Keep the SSR fallback conservative so dense
    // workspace content is never briefly exposed before the viewport is known.
    if (typeof window === 'undefined') {
      return { isSupported: false, isPortraitTablet: false };
    }
    
    return evaluateWorkspaceViewport(window.innerWidth, window.innerHeight);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewportState = () => {
      const next = evaluateWorkspaceViewport(window.innerWidth, window.innerHeight);

      setViewportState(prev => {
        if (prev.isSupported === next.isSupported && prev.isPortraitTablet === next.isPortraitTablet) {
          return prev;
        }
        return next;
      });
    };

    updateViewportState();

    window.addEventListener('resize', updateViewportState);
    window.addEventListener('orientationchange', updateViewportState);

    return () => {
      window.removeEventListener('resize', updateViewportState);
      window.removeEventListener('orientationchange', updateViewportState);
    };
  }, []);

  return viewportState;
}
